<template>
    <div 
        class="brick-root" 
        :class="[isActive ? 'active' : 'undefined', brickTypeName]"
        :data-jc-connection-id="connection.id"
        @mousedown="registerClickStart"
    >
        <brick-circle-base class="journey-brick" v-if="deduplicationTargetEnabled" @click="dedupTargetClick" v-tooltipped:[deduplicationTargetTooltip]="tooltipsEnabled">
            <div class="brick-icon">
                <img :src="DedupTargetIcon" />
            </div>
        </brick-circle-base>

        <brick-circle-base class="journey-brick" v-if="deduplicationBaseEnabled" @click="dedupBaseClick" v-tooltipped:[deduplicationBaseTooltip]="tooltipsEnabled">
            <div class="brick-icon">
                <img :src="DedupBaseIcon" />
            </div>
        </brick-circle-base>

        <brick-circle-base class="journey-brick" v-if="delayEnabled" @click="delayClick" v-tooltipped:[delayTooltip]="tooltipsEnabled">
            <div class="brick-icon">
                <i class="fas fa-clock" />
            </div>
        </brick-circle-base>
        
        <brick-circle-base class="journey-brick" v-if="timerEnabled" @click="timerClick" v-tooltipped:[timerTooltip]="tooltipsEnabled">
            <div class="brick-icon">
                <i class="fas fa-hourglass-half" />
            </div>
        </brick-circle-base>
        
        <div class="journey-filter-checkpoint" v-if="filterEnabled">
            <brick-circle-base class="journey-brick" @click="filterClick" v-tooltipped:[filterTooltip]="tooltipsEnabled">
                <div class="brick-icon">
                    <i class="fas fa-map-signs" />
                </div>
            </brick-circle-base>

            <div class="brick-icon interaction-filter-without-delay-warning" v-if="interactionFilterWithoutDelayTooltip" v-tooltipped:[interactionFilterWithoutDelayTooltip]="tooltipsEnabled">
                <i class="fas fa-exclamation"></i>
            </div>
        </div>
        
        <brick-circle-base class="journey-brick journey-filter-exclusion-checkpoint" v-if="filterExclusionEnabled"                      
            @click="filterExclusionClick" 
            v-tooltipped:[filterExclusionTooltip]="tooltipsEnabled" 
        >
            <div class="brick-icon">
                <span class="fa-stack fa-1x">
                    <i class="far fa-list-alt fa-stack-1x"></i>
                    <i class="fas fa-ban fa-stack-2x"></i>
                </span>
            </div>
        </brick-circle-base>
        
        <brick-circle-base class="journey-brick" @click="basicSettingsClick" v-tooltipped:[optionsTooltip]="tooltipsEnabled">
            <div class="brick-icon">
                <i class="fas fa-bars" />
            </div>
        </brick-circle-base>

        <component v-if="settingsOpen"
                    :is="editorType"
                    :connection="connection"
                    :config="config"
                    :is-new="isEditingNewCheckpoint"
                   
                    @save="save"
                    @hidden="closeSettings"
                    @change-status="statusChanged"
                    @delete-connection-checkpoint="deleteCheckpoint"
                    @delete-connection="deleteConnection"
        />
    </div>
</template>

<script>
    import resources from '@core/Util/Mixins/resources';
    import snowplow from "@modules/JourneyBuilder/snowplow";
    import {
        delayCheckpoint, 
        deduplicationTargetCheckpoint,
        deduplicationBaseCheckpoint,
        filterCheckpoint, 
        filterExclusionCheckpoint,
        optionsCheckpoint,
        timerCheckpoint
    } from "@modules/JourneyBuilder/Bricks/Checkpoints";

    import journeyBrickMixin from '@modules/JourneyBuilder/Mixins/journey-brick'
    import permissionsMixin from '@modules/JourneyBuilder/Mixins/journey-permissions';
    
    import tooltipped from "@modules/JourneyBuilder/directives/tooltipped";

    import BrickCircleBase from "@modules/JourneyBuilder/Components/Patterns/BrickCircleBase.vue";
    import {prompt} from "@modules/JourneyBuilder/Util/prompt";

    import {
        ConnectionSettings,
        
        DedupBaseSettings,
        DedupTargetSettings,
        DelaySettings,
        FilterSettings,
        FilterExclusionSettings,
        TimerSettings
    } from "./Settings";
    
    import DedupBaseIcon from "@core/Modules/JourneyBuilder/Assets/Checkpoints/icon_dedup_B.png";
    import DedupTargetIcon from "@core/Modules/JourneyBuilder/Assets/Checkpoints/icon_dedup_T.png";

    const nameToSettingsComponentMap = {
        [filterCheckpoint.name]: FilterSettings,
        [optionsCheckpoint.name]: ConnectionSettings,
        [delayCheckpoint.name]: DelaySettings,
        [deduplicationTargetCheckpoint.name]: DedupTargetSettings,
        [deduplicationBaseCheckpoint.name]: DedupBaseSettings,
        [filterExclusionCheckpoint.name]: FilterExclusionSettings,
        [timerCheckpoint.name]: TimerSettings
    };

    const hasMoved = (previous, current) => {
        const deltaX = previous.x - current.x;
        const deltaY = previous.y - current.y;

        return (deltaX * deltaX + deltaY * deltaY) > 10;
    };
    
    export default {
        name: 'brick-circle',
        components: {
            BrickCircleBase
        },
        mixins: [journeyBrickMixin, permissionsMixin, resources],
        directives: {
            tooltipped
        },

        props: {
            connection: {
                type: Object,
                required: true
            },
            isDragging: {
                type: Boolean,
                required: false
            }
        },

        data() {
            return {
                settingsOpen: false,
                editorType: null,

                DedupBaseIcon,
                DedupTargetIcon,

                clickStart: {},
                isEditingNewCheckpoint: false
            };
        },
        
        computed: {
            filterEnabled(){
                return filterCheckpoint.isActiveForConnection(this.connection);
            },
            delayEnabled(){
                return delayCheckpoint.isActiveForConnection(this.connection);
            },
            deduplicationTargetEnabled(){
                return deduplicationTargetCheckpoint.isActiveForConnection(this.connection);
            },
            deduplicationBaseEnabled(){
                return deduplicationBaseCheckpoint.isActiveForConnection(this.connection);
            },
            filterExclusionEnabled(){
                return filterExclusionCheckpoint.isActiveForConnection(this.connection);
            },
            timerEnabled() {
                return timerCheckpoint.isActiveForConnection(this.connection);
            },
            optionsTooltip() {
                return optionsCheckpoint.buildTooltip(this.connection);
            },
            filterTooltip(){
                return filterCheckpoint.buildTooltip(this.connection);
            },
            delayTooltip(){
                return delayCheckpoint.buildTooltip(this.connection);
            },
            deduplicationTargetTooltip(){
                return deduplicationTargetCheckpoint.buildTooltip(this.connection);
            },
            deduplicationBaseTooltip(){
                return deduplicationBaseCheckpoint.buildTooltip(this.connection);
            },
            filterExclusionTooltip(){
                return filterExclusionCheckpoint.buildTooltip(this.connection);
            },
            timerTooltip() {
                return timerCheckpoint.buildTooltip(this.connection);
            },
            tooltipsEnabled(){
                return !this.isDragging;
            },
            interactionFilterWithoutDelayTooltip(){
                const message = filterCheckpoint.buildInteractionFiltersWithoutDelayMessage(this.connection);
                
                if(!message) {
                    return;
                }
                
                return {
                    title: this.resource('Shared_Caution'),
                    content: `<div><b>${this.resource('JourneyBuilder_FilterSettings_InteractionFilter_NoDelayCheckpoint_ConfirmationTitle')}</b><br/>${message}</div>`
                };
            }
        },
        
        watch: {
            filterEnabled(enabled) {
                if(enabled) {
                    this.isEditingNewCheckpoint = true;
                    this.openSettings(filterCheckpoint.name);
                }
            },
            delayEnabled(enabled) {
                if(enabled) {
                    this.isEditingNewCheckpoint = true;
                    this.openSettings(delayCheckpoint.name);
                }
            },
            deduplicationTargetEnabled(enabled) {
                if(enabled) {
                    this.isEditingNewCheckpoint = true;
                    this.openSettings(deduplicationTargetCheckpoint.name);
                }
            },
            deduplicationBaseEnabled(enabled) {
                if(enabled) {
                    this.isEditingNewCheckpoint = true;
                    this.openSettings(deduplicationBaseCheckpoint.name);
                }
            },
            timerEnabled(enabled) {
                if(enabled) {
                    this.isEditingNewCheckpoint = true;
                    this.openSettings(timerCheckpoint.name);
                }
            }
        },

        methods: {
            async openSettings(tab) {
                if (nameToSettingsComponentMap.hasOwnProperty(tab)) {
                    this.editorType = nameToSettingsComponentMap[tab];
                }

                this.settingsOpen = true;
                snowplow.trackStructuredEvent('open-connection-settings');
            },
            registerClickStart(evt) {
                this.clickStart = {
                    x: evt.clientX,
                    y: evt.clientY
                };
            },
            closeSettings() {
                //Upon closing the settings, the next edited checkpoint may not be considered new anymore
                this.isEditingNewCheckpoint = false;
                this.settingsOpen = false;
            },
            statusChanged(settings) {
                this.connection.startDate = settings.activeFrom;
                this.connection.isDisabled = !settings.active;
                this.connection.config.deactivationOptions = settings.deactivationOptions;
                this.closeSettings();
            },
            deleteConnection($event){
                this.closeSettings();
                this.$emit('delete-connection', $event)
            },
            deleteCheckpoint($event){
                this.closeSettings();
                this.$emit('delete-connection-checkpoint', $event);  
            },
            async save($event){
                this.closeSettings();
                this.$emit('change-connection', $event);
                
                //Wait for our UI/interactionFilterWithoutDelayTooltip value to update
                await this.$nextTick();
                
                //Filter checkpoints might trigger a popup upon being saved
                if(
                    !$event[filterCheckpoint.name] ||
                    !this.interactionFilterWithoutDelayTooltip
                )
                {
                    return;
                }

                //'Abuse' our prompt to simply display a message
                try {
                    await prompt(
                        this.interactionFilterWithoutDelayTooltip.title,

                        //Render our content as HTML
                        { template: this.interactionFilterWithoutDelayTooltip.content },
                        { buttons: [] }
                    );    
                } catch(e) {}
            },
            handleCheckpointClick(evt, checkpoint) {
                if (!hasMoved(this.clickStart, { x: evt.clientX, y: evt.clientY })) {
                    this.openSettings(checkpoint.name);
                }
            },
            filterClick(evt) {
                this.handleCheckpointClick(evt, filterCheckpoint);
            },
            delayClick(evt) {
                this.handleCheckpointClick(evt, delayCheckpoint);
            },
            dedupTargetClick(evt) {
                this.handleCheckpointClick(evt, deduplicationTargetCheckpoint);
            },
            dedupBaseClick(evt) {
                this.handleCheckpointClick(evt, deduplicationBaseCheckpoint);
            },
            filterExclusionClick(evt){
                this.handleCheckpointClick(evt, filterExclusionCheckpoint);
            },
            timerClick(evt) {
                this.handleCheckpointClick(evt, timerCheckpoint);
            },
            basicSettingsClick(evt){
                this.handleCheckpointClick(evt, optionsCheckpoint);
            }
        }
    }
</script>

<style scoped lang="scss">
    @import "~@modules/JourneyBuilder/Style/journey-brick-circle.scss";
</style>